import React, { useState } from 'react';
import FadeIn from 'react-fade-in';
import _ from 'underscore';

import FloatingButton from '../../components/FloatingButton';
import { Header } from '../../components/Header';
import LoaderButton from '../../components/LoaderButton';
import CustomDatePicker, { DateRangeOptions } from '../../components/CustomDatePicker';
import GenericTable from '../../components/GenericTable';
import FieldWithHistory from '../../components/FieldWithHistory';

import { getVINLog } from '../../libs/db-lib';

import {
  allFilterKeys,
} from '../../libs/tables/logTableStructure';
import {
  getRecentSearches,
  setRecentSearches,
  storeClipboard,
} from '../../libs/utils-ts';
import { calculateStartAndEndDate } from '../../libs/utils';

import { baseLogColumns } from '../../libs/tables/columnDefinitions';
import { LogSubcomponent } from '../../libs/tables/subComponents';

import { ChildProps } from '../../types';

import CopyData from '../../components/CopyData';

const GetVINLog = (props: ChildProps) => {
  const [vin, setVin] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shopID, setShopID] = useState<string>('');
  const [showLog, setShowLog] = useState<boolean>(false);
  const [log, setLog] = useState<any[]>([]);
  const [rangePicked, setRangePicked] = useState<DateRangeOptions>('last30Days');

  let pageData: { [k: string]: any } = {
    'VIN': vin,
    'Date Range': `${rangePicked}`,
    'VIN Log Count': log?.length || 0,
    'VIN Log': log,
  };

  storeClipboard(pageData);

  const topRef = React.createRef<HTMLDivElement>();

  const validateForm = () => {
    return vin?.length > 0;
  };

  const handleSubmit = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    setShowLog(false);
    setIsLoading(true);
    setLog([]);
    const { startDateStr, endDateStr } = calculateStartAndEndDate(rangePicked);

    let result = await getVINLog(
      vin,
      shopID,
      startDateStr,
      endDateStr
    );

    if (!result || Object.hasOwn(result, 'error')) {
      if ([401, 403].indexOf(result.error.status) === -1) {
        props.handleShowAlert('Error', result.error);
      } else {
        return;
      }
      if (result.error.message) {
        props.handleShowAlert('Error', result.error.message);
      } else {
        props.handleShowAlert('Error', JSON.stringify(result.error));
      }
    } else if (result.logEntries?.length === 0) {
      props.handleShowAlert(
        'Get Logs',
        <span>
          No logs found for VIN: <strong>{vin}</strong> in that date
          range.
        </span>,
        false
      );
    } else {
      setLog(result.logEntries);
      setShowLog(true);
    }

    setIsLoading(false);

    let recentSearches = getRecentSearches("VIN");

    if (recentSearches) {
      if (recentSearches.length > 4) recentSearches.pop();
      if (vin && recentSearches.indexOf(vin) === -1)
        recentSearches.unshift(vin);
    } else {
      recentSearches = vin ? [vin] : [];
    }
    setRecentSearches(vin, recentSearches);
  };

  return (
    <>
      <Header context="Shop Functions" title="Get VIN Log" />
      <FloatingButton
        onClick={() => {
          if (topRef && topRef.current) {
            topRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }}
      />
      <div className="l-container" ref={topRef}>
        <form onSubmit={handleSubmit}>
          <div className={`c-field`}>
            <label
              htmlFor="vin"
              className="c-field__label"
            >
              VIN
            </label>
            <FieldWithHistory
              fieldId="vin"
              searchKey="VIN"
              selected={vin}
              onInputChange={(e) => {
                if (e !== undefined) {
                  e = e.toUpperCase();
                  setVin(e.trim());
                }
              }}
              onChange={(e) => {
                if (e !== undefined) {
                  e = e.toUpperCase();
                  setVin(e.trim());
                }
              }}
              placeholder="17 characters long"
              onEnter={handleSubmit}
            />
          </div>
          <div className="c-field">
            <label
              htmlFor="query"
              className="c-field__label u-margin-top-large"
            >
              Shop ID (optional)
            </label>
            <FieldWithHistory
              fieldId="shopID"
              searchKey={'SHOP ID'}
              selected={shopID}
              onInputChange={(shopIDInput) => {
                if (shopIDInput !== undefined) {
                  setShopID(shopIDInput.toLowerCase().trim());
                }
              }}
              onChange={(shopIDInput) => {
                if (shopIDInput !== undefined) {
                  setShopID(shopIDInput.toLowerCase().trim());
                }
              }}
              placeholder="ex. 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'"
              onEnter={handleSubmit}
            />
          </div>
          <div className="c-field">
            <label
              htmlFor="dateRange"
              className="c-field__label u-margin-top-large"
            >
              Date Range
            </label>
            <CustomDatePicker
              rangePicked={rangePicked}
              setRangePicked={setRangePicked}
            />
          </div>

          <div className="c-field">
            <LoaderButton
              type="submit"
              disabled={!validateForm()}
              isLoading={isLoading}
              text="Get Log"
              loadingText="Searching..."
            />
          </div>
        </form>
      </div>
      <FadeIn visible={!isLoading && showLog}>
        <div className="u-text-center">
          {vin && (
            <>
              <h2 className="l-flex-center u-overflow-wrap-anywhere">
                Log Table For VIN:{' '}{vin}
              </h2>
              <CopyData info={pageData}/>
              {shopID && (
                <>
                  <br />
                  <h2>
                    Shop ID:{' '}
                    <span className="u-font-mono">{shopID}</span>
                  </h2>
                </>
              )}
            </>
          )}
        </div>

        <GenericTable
          id="VIN_LOG-table"
          data={log || []}
          filterKeys={allFilterKeys}
          columnDefs={baseLogColumns}
          defaultSorted={[
            {
              id: 'actionDate',
              desc: true,
            },
          ]}
          savedColumnsId={'fullLogTableColumns'}
          subComponentRenderer={LogSubcomponent}
        />
      </FadeIn>
    </>
  );
};

export default GetVINLog;
